import { useCallback } from 'react';
import type { Project } from '@/api/client';
import {
  PROJECT_TABLE_FILTERS_ENABLED_KEY,
  PROJECT_TABLE_FILTERS_KEY,
  normalizeProjectFilterRuleOnLoad,
  projectMatchesRule,
  type TableFiltersContextValue,
} from '@/lib/tableFilters';
import {
  useEntityTableFilters,
  type UseEntityTableFiltersReturn,
} from '@/hooks/useEntityTableFilters';

export interface ProjectTableFiltersContextValue extends TableFiltersContextValue<Project> {
  filterProjects: (projects: Project[]) => Project[];
  filterProjectIds: (
    projectIds: string[],
    resolveProject: (projectId: string) => Project | undefined
  ) => string[];
}

export interface UseProjectTableFiltersReturn
  extends UseEntityTableFiltersReturn<Project>,
    ProjectTableFiltersContextValue {}

export function useProjectTableFilters(): UseProjectTableFiltersReturn {
  const entityFilters = useEntityTableFilters<Project>({
    enabledKey: PROJECT_TABLE_FILTERS_ENABLED_KEY,
    filtersKey: PROJECT_TABLE_FILTERS_KEY,
    normalizeLoadedRule: normalizeProjectFilterRuleOnLoad,
    matchesRule: projectMatchesRule,
  });

  const filterProjectIds = useCallback(
    (projectIds: string[], resolveProject: (projectId: string) => Project | undefined): string[] => {
      const projects = projectIds
        .map((projectId) => {
          const project = resolveProject(projectId);
          return project ? ({ projectId, project } as const) : null;
        })
        .filter((entry): entry is { projectId: string; project: Project } => entry !== null);

      const allowedIds = new Set(
        entityFilters.filterItems(projects.map((entry) => entry.project)).map((project) => project.id)
      );

      return projects
        .filter(({ project }) => allowedIds.has(project.id))
        .map(({ projectId }) => projectId);
    },
    [entityFilters]
  );

  return {
    ...entityFilters,
    filterProjects: entityFilters.filterItems,
    filterProjectIds,
  };
}
